import React from "react"

interface Task {
  id: string
  title: string
  body?: string
  status: number
  priority: number
  assigneeId?: string
  assigneeName?: string
  createdBy: string
  dueDate?: string
  createdAt: string
  updatedAt: string
}

interface TasksListProps {
  tasks: Task[]
  isLoading: boolean
  onTaskSelect: (taskId: string) => void
  onUpdateTask: (taskId: string, data: any) => void
  selectedTaskId: string | null
  updateTaskLoading: boolean
}

const getStatusName = (status: number) => {
  switch (status) {
    case 0:
      return "Todo"
    case 1:
      return "In Progress"
    case 2:
      return "Done"
    default:
      return "Unknown"
  }
}

const getStatusColor = (status: number) => {
  switch (status) {
    case 0:
      return "bg-gray-100 text-gray-800"
    case 1:
      return "bg-blue-100 text-blue-800"
    case 2:
      return "bg-green-100 text-green-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

const getPriorityName = (priority: number) => {
  switch (priority) {
    case 0:
      return "Low"
    case 1:
      return "Normal"
    case 2:
      return "High"
    case 3:
      return "Urgent"
    default:
      return "Normal"
  }
}

const getPriorityColor = (priority: number) => {
  switch (priority) {
    case 0:
      return "bg-green-100 text-green-800"
    case 1:
      return "bg-gray-100 text-gray-800"
    case 2:
      return "bg-orange-100 text-orange-800"
    case 3:
      return "bg-red-100 text-red-800"
    default:
      return "bg-gray-100 text-gray-800"
  }
}

export const TasksList: React.FC<TasksListProps> = ({
  tasks,
  isLoading,
  onTaskSelect,
  onUpdateTask,
  selectedTaskId,
  updateTaskLoading,
}) => {
  const handleStatusChange = (taskId: string, newStatus: number) => {
    onUpdateTask(taskId, { status: newStatus })
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {Array.from({ length: 5 }).map((_, i) => (
          <div key={i} className="bg-card rounded-lg border p-4 animate-pulse">
            <div className="h-5 bg-muted rounded mb-2" />
            <div className="h-4 bg-muted rounded w-3/4 mb-2" />
            <div className="flex gap-2">
              <div className="h-6 bg-muted rounded w-16" />
              <div className="h-6 bg-muted rounded w-16" />
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (tasks.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-6xl mb-4">✅</div>
        <h3 className="text-lg font-medium text-foreground mb-2">No tasks yet</h3>
        <p className="text-muted-foreground">Create your first task to get started</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {tasks.map((task) => (
        <div
          key={task.id}
          onClick={() => onTaskSelect(task.id)}
          className={`bg-card rounded-lg border p-4 cursor-pointer transition-colors hover:bg-secondary ${
            selectedTaskId === task.id ? "ring-2 ring-primary bg-secondary" : ""
          }`}
        >
          <div className="flex justify-between items-start mb-2">
            <h3 className="font-semibold text-foreground">{task.title}</h3>
            <div className="flex gap-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                {getStatusName(task.status)}
              </span>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                {getPriorityName(task.priority)}
              </span>
            </div>
          </div>

          {task.body && <p className="text-muted-foreground text-sm mb-2 line-clamp-2">{task.body}</p>}

          <div className="flex justify-between items-center">
            <div className="flex gap-4 text-xs text-muted-foreground">
              {task.assigneeName && <span>👤 {task.assigneeName}</span>}
              {task.dueDate && <span>📅 {new Date(task.dueDate).toLocaleDateString()}</span>}
            </div>

            {/* Quick status change buttons */}
            <div className="flex gap-1">
              {[0, 1, 2].map((status) => (
                <button
                  key={status}
                  onClick={(e) => {
                    e.stopPropagation()
                    handleStatusChange(task.id, status)
                  }}
                  disabled={updateTaskLoading || task.status === status}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    task.status === status
                      ? getStatusColor(status)
                      : "bg-muted hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground"
                  } disabled:opacity-50`}
                  title={`Mark as ${getStatusName(status)}`}
                >
                  {status === 0 ? "📋" : status === 1 ? "🔄" : "✅"}
                </button>
              ))}
            </div>
          </div>

          <p className="text-xs text-muted-foreground mt-2">Updated {new Date(task.updatedAt).toLocaleDateString()}</p>
        </div>
      ))}
    </div>
  )
}
